import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "../lib/utils.js";
import { FloodCard, floodCardSlots, type FloodCardProps, type FloodCardTone } from "./ui/flood-card.js";

// Resting colors of the icon box; on hover it flips to white with the flood.
const ICON_TONES: Record<FloodCardTone, string> = {
  brand:   "bg-brand-100 text-brand-600",
  info:    "bg-[var(--color-info-bg)] text-[var(--color-info-fg)]",
  success: "bg-[var(--color-success-bg)] text-[var(--color-success-fg)]",
  warning: "bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]",
  danger:  "bg-[var(--color-error-bg)] text-[var(--color-error-fg)]",
  accent:  "bg-[var(--color-accent-bg)] text-[var(--color-accent-fg)]",
  rose:    "bg-[var(--color-rose-bg)] text-[var(--color-rose-fg)]",
  cyan:    "bg-[var(--color-cyan-bg)] text-[var(--color-cyan-fg)]",
};

export interface SummaryStatCardProps {
  icon: LucideIcon;
  label: ReactNode;
  /** The headline value; wrap a unit/suffix in <SummaryStatSuffix>. */
  value: ReactNode;
  caption?: ReactNode;
  /** Flood color on hover. */
  tone?: FloodCardTone;
  /** Resting icon box color; defaults to `tone`. */
  iconTone?: FloodCardTone;
  /** "stacked" (default) is the tall tile; "row" is the compact bar -- icon, then
   *  label over value, with the caption pushed to the right edge. */
  layout?: "stacked" | "row";
  /** "row" only: let a long label wrap onto a second line instead of being cut off with "…". */
  wrapLabel?: boolean;
  /** Extra classes for the value, e.g. a smaller size or a status color. */
  valueClassName?: string;
  className?: string;
  /** Pass-through FloodCard look (speed, orb, lift, surface, dimensions…). */
  card?: Omit<FloodCardProps, "tone" | "children" | "className">;
}

/** Icon + uppercase label + headline value + caption, on a hover-flood tile. */
export function SummaryStatCard({ icon: Icon, label, value, caption, tone = "brand", iconTone, layout = "stacked", wrapLabel = false, valueClassName, className, card }: SummaryStatCardProps) {
  const row = layout === "row";
  return (
    // Straight FloodCard defaults -- tinted surface, resting orb, xl radius --
    // so a summary tile reads exactly like a leave balance card: the tone
    // washes the whole card, with the accent glowing at its lower edge.
    // Override any of it per call site through `card`.
    // `row` keeps every tone, slot and flood and only changes the arrangement and
    // scale, which is why it is a variant here rather than a second component.
    <FloodCard tone={tone} {...(row ? { size: "sm" as const } : {})} {...card} className={cn(row && "flex items-center gap-3", className)}>
      <div className={cn(
        "flex items-center justify-center rounded-[var(--radius-md)]",
        row ? "h-7 w-7 shrink-0" : "mb-2 h-8 w-8",
        ICON_TONES[iconTone ?? tone], floodCardSlots.icon,
      )}>
        <Icon size={row ? 14 : 16} />
      </div>
      <div className={cn(row && "min-w-0")}>
        <div className={cn("font-semibold uppercase tracking-[0.5px] text-surface-700", row ? cn("text-[10px]", wrapLabel ? "leading-tight" : "truncate whitespace-nowrap") : "text-[11px]", floodCardSlots.muted)}>{label}</div>
        <div className={cn("mt-0.5 font-bold", row ? "text-[16px] leading-tight" : "text-[22px]", floodCardSlots.strong, valueClassName)}>{value}</div>
        {caption && !row && <div className={cn("mt-0.5 text-[11px] text-surface-700", floodCardSlots.muted)}>{caption}</div>}
      </div>
      {caption && row && (
        // Capped at half the bar so a long caption wraps instead of squeezing the value.
        <div className={cn("ml-auto max-w-[50%] pl-3 text-right text-[11px] leading-tight text-surface-700", floodCardSlots.muted)}>{caption}</div>
      )}
    </FloodCard>
  );
}

/** Smaller, quieter unit after a SummaryStatCard value (" / 5", " hrs"). */
export function SummaryStatSuffix({ children }: { children: ReactNode }) {
  return <span className={cn("text-sm font-medium text-surface-700", floodCardSlots.muted)}>{children}</span>;
}
